import { Injectable } from '@angular/core';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, interval, merge, Observable, switchMap } from 'rxjs';
import { AlertsDemoData } from 'src/app/modules/orchestration/alerts/alerts.demo.data.type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlertSeverity } from '../enums/alert-severity.enum';
import { DateHelper, DateTimeFormat } from '../helpers/date.helper';
import { NotificationBarMessage, NotificationBarMessageType } from '../interfaces/notification-bar-message.interface';
import { ApiService } from './api/api.service';
import { AppConfigurationService } from './app-configuration.service';
import { AuthService } from './auth.service';
import { OrganizationService } from './organization.service';
import { TenantsService } from './tenants.service';
import { SchedulerStatus } from '../types/generated.types';
import { ClientPermission, FeatureFlags, Platform, PlatformDescription, TenantSynchronizationMode } from '../enums/generated.enums';
import { TenantConfigurationUi } from '../types/tenant.type';
import { NavigationService } from './navigation.service';
import { SubPageType } from '../enums/page-type.enum';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private unreadAlertsCountSubject$ = new BehaviorSubject<number>(0);
  private unreadAnomaliesCountSubject$ = new BehaviorSubject<number>(0);
  private notificationBarMessagesSubject = new BehaviorSubject<NotificationBarMessage[]>([]);
  private lastLicenseLimitCheck: number;
  private licenseLimitViolationMessages: NotificationBarMessage[];

  get notificationBarMessages$(): Observable<NotificationBarMessage[]> {
    return this.notificationBarMessagesSubject.asObservable();
  }

  get unreadAlertsCount$(): Observable<number> {
    return this.unreadAlertsCountSubject$.pipe(distinctUntilChanged());
  }

  get unreadAnomaliesCount$(): Observable<number> {
    return this.unreadAnomaliesCountSubject$.pipe(distinctUntilChanged());
  }

  get areAlertsEnabled(): boolean {
    return this.appConfigurationService.isOnPremise || this.organizationService.isDemoOrganization;
  }

  get isDemoOrganization(): boolean {
    return this.organizationService.isDemoOrganization;
  }

  /** The Alerts command view: the badge means active problems, and nothing is ever marked as read. */
  get isNotificationsV2(): boolean {
    return this.authService.hasFeatureFlag(FeatureFlags.NotificationsV2);
  }

  constructor(
    private apiService: ApiService,
    private appConfigurationService: AppConfigurationService,
    private authService: AuthService,
    private organizationService: OrganizationService,
    private tenantsService: TenantsService,
    private navigationService: NavigationService,
  ) {
    void this.refreshNotifications();

    merge(
      this.tenantsService.selectedTenantId$,
      interval(30000),
    )
      .pipe(
        takeUntilDestroyed(),
        filter(() => this.authService.isLoggedIn),
        debounceTime(500),
        switchMap(() => this.refreshNotifications()),
      )
      .subscribe();
  }

  private async refreshNotifications(): Promise<void> {
    const messages: NotificationBarMessage[] = [
      this.getInvalidLicenseMessage(),
      this.getPointeeLicenseExpirationMessage(),
      ...this.getOrchestrationLicenseCountInvalidMessageForAllTenants(),
    ];

    if (this.appConfigurationService.isOnPremise) {
      messages.push(...await this.getSchedulerStatusMessagesForAllTenants());
    }

    if (this.lastLicenseLimitCheck !== DateHelper.startOfToday().getTime()) {
      this.lastLicenseLimitCheck = DateHelper.startOfToday().getTime();
      this.licenseLimitViolationMessages = await this.getLicenseLimitViolationMessagesForAllTenants();
    }
    if (this.licenseLimitViolationMessages) {
      messages.push(...this.licenseLimitViolationMessages);
    }

    this.notificationBarMessagesSubject.next(messages.filter(m => m != null));

    if (this.areAlertsEnabled) {
      if (this.isNotificationsV2) {
        this.unreadAlertsCountSubject$.next(await this.apiService.alerts.getActiveProblemsCount(this.tenantsService.selectedTenantId));
      } else {
        let unreadCount = await this.apiService.notifications.getUnreadCount(this.tenantsService.selectedTenantId);
        if (this.isDemoOrganization) {
          unreadCount += AlertsDemoData.notifications.filter(n => !n.isRead).length;
        }
        this.unreadAlertsCountSubject$.next(unreadCount);
      }
    }

    if (this.authService.hasPermission(ClientPermission.FastAnomalyDetection)) {
      const unreadCount = await this.apiService.anomalies.getUnreadCount(this.tenantsService.selectedTenantId);
      this.unreadAnomaliesCountSubject$.next(unreadCount);
    }
  }

  /** After a mute or an unmute the badge has to agree with the page at once, not at the next poll. */
  async refreshAlertsCount(): Promise<void> {
    if (this.areAlertsEnabled && this.isNotificationsV2) {
      this.unreadAlertsCountSubject$.next(await this.apiService.alerts.getActiveProblemsCount(this.tenantsService.selectedTenantId));
    }
  }

  async markAllAlertsAsRead(): Promise<void> {
    if (this.organizationService.isDemoOrganization) {
      AlertsDemoData.notifications.forEach(n => (n.isRead = true));
    }

    await this.apiService.notifications.markAllAsRead(this.tenantsService.selectedTenantId);
    this.unreadAlertsCountSubject$.next(0);
  }

  async markAllAnomaliesAsRead(): Promise<void> {
    await this.apiService.anomalies.markAllAsRead(this.tenantsService.selectedTenantId);
    this.unreadAnomaliesCountSubject$.next(0);
  }

  async markAnomalyAsRead(anomalyId: string, isRead: boolean): Promise<void> {
    await this.apiService.anomalies.markAsRead(anomalyId, isRead);
    const currentCount = this.unreadAnomaliesCountSubject$.value;
    this.unreadAnomaliesCountSubject$.next(Math.max(0, currentCount + (isRead ? -1 : 1)));
  }

  private getOrchestrationLicenseCountInvalidMessageForAllTenants(): NotificationBarMessage[] {
    const selectedTenantId = this.tenantsService.selectedTenantId;
    const tenantConfigs = this.tenantsService.tenantConfigurations.filter(t => !selectedTenantId || t.tenantId === selectedTenantId);
    return tenantConfigs.map(t => this.getOrchestrationLicenseCountInvalidMessage(t));
  }

  private getOrchestrationLicenseCountInvalidMessage(tenantConfig: TenantConfigurationUi): NotificationBarMessage {
    if (!this.authService.hasPermission(ClientPermission.Orchestration)
      || tenantConfig.hasLicensesAssignedForOrchestration
      || tenantConfig.synchronizationMode !== TenantSynchronizationMode.None) {
      return null;
    }

    const platformLabel = PlatformDescription.get(tenantConfig.platform);
    const tenantLabel = this.tenantsService.getTenantLabel(tenantConfig.tenantId);
    const onTenant = this.tenantsService.isAllTenantsSelected ? ` on the tenant <i>${tenantLabel}</i>` : '';

    return {
      type: NotificationBarMessageType.LicenseCountInvalid,
      severity: AlertSeverity.Warning,
      htmlText: `<b>There are no ${platformLabel} licenses assigned to Pointee orchestration${onTenant}.</b> Pointee cannot start processes.`,
      icon: 'warning-triangle',
      tooltip: null,
      contactSupportEnabled: false,
      link: this.authService.hasPermission(ClientPermission.OrganizationSettings)
        ? {
          text: 'Change settings',
          route: this.navigationService.getOrganizationSettingsRoute(SubPageType.TenantsSettings),
          queryParams: { tenantId: tenantConfig.tenantId },
        }
        : null,
    };
  }

  private async getSchedulerStatusMessagesForAllTenants(): Promise<NotificationBarMessage[]> {
    const messages: NotificationBarMessage[] = [];
    const selectedTenantId = this.tenantsService.selectedTenantId;

    try {
      const schedulerStatuses = await this.apiService.scheduler.getSchedulerStatus();
      for (const status of schedulerStatuses.filter(s => !selectedTenantId || s.tenantId === selectedTenantId)) {
        const tenantConfig = this.tenantsService.getTenantConfiguration(status.tenantId);
        if (tenantConfig) {
          messages.push(...this.getSchedulerStatusMessages(status, tenantConfig));
        }
      }
      return messages;
    } catch (error) {
      console.error('Failed to get scheduler status messages:', error);
      return [];
    }
  }

  private async getLicenseLimitViolationMessagesForAllTenants(): Promise<NotificationBarMessage[]> {
    const violations = await this.apiService.monitoring.getLicenseLimitViolations();
    return violations.map(v => ({
      type: NotificationBarMessageType.LicenseLimitViolation,
      icon: 'warning-triangle',
      severity: AlertSeverity.Warning,
      htmlText: `The number of your ${PlatformDescription.get(v.platform)} licenses (${v.value}) exceeds the number allowed in your Pointee plan (${v.limit}).`,
      contactSupportEnabled: true,
    }));
  }

  private getSchedulerStatusMessages(schedulerStatus: SchedulerStatus, tenantConfig: TenantConfigurationUi): NotificationBarMessage[] {
    const allTenants = this.tenantsService.isAllTenantsSelected;
    const tenantLabel = this.tenantsService.getTenantLabel(tenantConfig.tenantId);
    const ofTenant = allTenants ? ` of the tenant <i>${tenantLabel}</i>` : ''; // HATE OH :-)
    const onTenant = allTenants ? ` on the tenant <i>${tenantLabel}</i>` : '';

    const messages: NotificationBarMessage[] = [];
    if (schedulerStatus?.isSchedulerRunning === false) {
      messages.push(this.createNotificationBarMessage(
        NotificationBarMessageType.SchedulerDisabled,
        schedulerStatus.tenantId,
        `<b>Orchestration or synchronization${onTenant} is not enabled or has not been initiated yet.</b> If the problem persists for more than a few minutes, please contact support.`,
      ));
    }

    if (schedulerStatus?.isSchedulerRunning === true && schedulerStatus?.isRpaDatabaseConnected === false) {
      messages.push(this.createNotificationBarMessage(
        NotificationBarMessageType.SchedulerRpaDbNotConnected,
        schedulerStatus.tenantId,
        `<b>Connection to ${this.getDataConnectionTarget(tenantConfig.platform)}${ofTenant} has been lost.</b> The data you see may not be up to date.`,
      ));
    }

    if (schedulerStatus?.isSchedulerRunning === true && schedulerStatus?.isRpaServerConnected === false) {
      messages.push(this.createNotificationBarMessage(
        NotificationBarMessageType.SchedulerRpaServerNotConnected,
        schedulerStatus.tenantId,
        `<b>Pointee is unable to start and stop processes${ofTenant}.</b> Connection to ${this.getControlConnectionTarget(tenantConfig.platform)} has been lost.`,
      ));
    }

    if (schedulerStatus?.isSchedulerRunning === true && schedulerStatus?.isSchedulerFastEnough === false && tenantConfig.displaySlowSchedulerWarning) {
      messages.push(this.createNotificationBarMessage(
        NotificationBarMessageType.SchedulerIsSlow,
        schedulerStatus.tenantId,
        `<b>Orchestration${onTenant} is slow.</b> Some processes may not be started on time.`,
        AlertSeverity.Warning,
      ));
    }

    return messages;
  }

  // what the data synchronization reads from - only Blue Prism (classic) is read from its database
  private getDataConnectionTarget(platform: Platform): string {
    switch (platform) {
      case Platform.BluePrism:
        return 'the Blue Prism database';
      case Platform.UiPath:
        return 'UiPath Orchestrator';
      default:
        return `the ${PlatformDescription.get(platform)} API`;
    }
  }

  // what processes are started and stopped through - Blue Prism (classic) uses AutomateC, the others the same API as the synchronization
  private getControlConnectionTarget(platform: Platform): string {
    return platform === Platform.BluePrism
      ? 'Blue Prism through AutomateC'
      : this.getDataConnectionTarget(platform);
  }

  /**
   * Surfaces a server license that failed to parse, or that references permissions this version no longer recognizes,
   * so an admin is not left with a silently broken app. Shown only to users who can act on it.
   */
  private getInvalidLicenseMessage(): NotificationBarMessage {
    if (!this.authService.hasPermission(ClientPermission.OrganizationSettings)) {
      return null;
    }

    if (this.authService.user?.licenseInvalid) {
      return {
        type: NotificationBarMessageType.LicenseInvalid,
        tenantId: null,
        icon: 'warning-triangle',
        severity: AlertSeverity.Error,
        htmlText: '<b>Your Pointee license is invalid and could not be read.</b> The application is unusable until a valid license is provided. Please contact support.',
        contactSupportEnabled: true,
      };
    }

    const unrecognized = this.authService.user?.unrecognizedLicensePermissions ?? [];
    if (unrecognized.length > 0) {
      return {
        type: NotificationBarMessageType.LicenseUnrecognizedPermissions,
        tenantId: null,
        icon: 'warning-triangle',
        severity: AlertSeverity.Warning,
        htmlText: `<b>Your Pointee license references features this version no longer supports (${unrecognized.join(', ')}).</b> They were ignored. Please re-issue the license to remove them.`,
        contactSupportEnabled: true,
      };
    }

    return null;
  }

  private getPointeeLicenseExpirationMessage(): NotificationBarMessage {
    const expirationDate = this.appConfigurationService.licenseExpiration;
    if (!expirationDate) {
      return null;
    }

    const days = Math.max(0, DateHelper.getDaysCount(DateHelper.startOfToday(), DateHelper.startOfDay(expirationDate)) - 1);
    if (days >= this.appConfigurationService.licenseExpirationWarningDays) {
      return null;
    }

    return  {
      type: NotificationBarMessageType.LicenseExpiresSoon,
      tenantId: null,
      icon: 'warning-triangle',
      severity: AlertSeverity.Warning,
      htmlText: `Your Pointee license will expire in ${days} ${days > 1 ? 'days' : 'day'} (on ${DateHelper.formatDate(expirationDate, DateTimeFormat.ShortMonthNameDayYear)}).`,
      contactSupportEnabled: true,
    };
  }


  private createNotificationBarMessage(type: NotificationBarMessageType, tenantId: string, htmlText: string, severity = AlertSeverity.Error): NotificationBarMessage {
    return { type, tenantId, icon: 'warning-triangle', severity, htmlText, contactSupportEnabled: true };
  }
}
